/* eslint-disable consistent-return */
import React, { ButtonHTMLAttributes, forwardRef, ReactElement, Ref, useState } from 'react'

import classNames from 'classnames'

import { useNavigation } from '@web/hooks/useNavigation'
import { useAbTestNClick } from '@web/libs/contexts/AbTestEventContextProvider'
import { useContent } from '@web/libs/contexts/ContentContext'
import { useClickCodeDebugMode } from '@web/libs/dev/clickCodeDebugMode.state'
import { useOverlayText } from '@web/libs/dev/useOverlayText'
import sendClickLog from '@web/libs/nlog/click/sendClickLog'
import { Nullish } from '@web/types'

import styles from './Clickable.module.scss'

export interface ClickableProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  href?: string
  replace?: boolean
  newWindow?: boolean
  nClickCode?: Nullish<string>
  nClickCid?: string | number
  nClickRank?: string | number
  disabled?: boolean
  stopPropagation?: boolean
}

const Clickable = (
  {
    className,
    children,
    href = '',
    replace = false,
    newWindow = false,
    onClick,
    nClickCode,
    nClickCid,
    nClickRank,
    type = 'button',
    disabled = false,
    stopPropagation = false,
    ...props
  }: ClickableProps,
  ref: Ref<HTMLButtonElement>,
): ReactElement => {
  const content = useContent()
  const { trackNClickEvent } = useAbTestNClick()
  const [isHovered, setIsHovered] = useState(false)
  const { push } = useNavigation()

  const [clickCodeDebugMode] = useClickCodeDebugMode()

  const { OverlayText } = useOverlayText({ showable: isHovered && !!clickCodeDebugMode, text: nClickCode })

  const handleClick: React.MouseEventHandler<HTMLButtonElement> = e => {
    e.preventDefault()

    if (stopPropagation) e.stopPropagation()

    if (disabled) return

    const isClickEvent = e?.type === 'click'
    if (nClickCode && isClickEvent) {
      sendClickLog({
        event: e,
        area: nClickCode,
        rank: nClickRank,
        contentId: nClickCid ?? content.id,
        contentType: content.type,
      })
      trackNClickEvent(nClickCode)
    }

    if (onClick) return onClick(e)

    if (href) {
      if (newWindow) return push({ url: href, openWindow: true })

      if (replace) return push({ url: href, replace: true })

      return push({ url: href })
    }
  }

  return (
    <button
      className={classNames(styles.clickable, className, { [styles.clickable_link]: !!href })}
      ref={ref}
      type={type}
      onClick={handleClick}
      disabled={disabled}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      {...props}
    >
      {children}
      {OverlayText}
    </button>
  )
}
Clickable.displayName = 'Clickable'
export default forwardRef(Clickable)
